/** D:\directiveai-react\frontend\src\components\AGTHall\services\AGTHallService.js */

import axios from 'axios';

/**
 * URL base para AGT Hall Backend (usuarios, pagos, archivos)
 * Puerto 5000 en desarrollo
 */
const AGT_HALL_API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_HALL_URL || 'https://6bsavxcfhg.execute-api.us-east-2.amazonaws.com/prod')
  : 'http://localhost:5010';

/**
 * URL base para AGT Chat Backend (proyectos)
 * Puerto 5001 en desarrollo
 */
const AGT_CHAT_API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_CHAT_URL || 'https://ore7ej9pa3.execute-api.us-east-2.amazonaws.com/prod')
  : 'http://localhost:5001';

/**
 * Asumimos un timeout estándar de 5 minutos (300000 ms)
 */
const STANDARD_TIMEOUT = 5000000; // 5 minutos en milisegundos

/**
 * Función para crear interceptores de error comunes
 */
const createErrorInterceptor = (instance) => {
  instance.interceptors.response.use(
    response => response,
    error => {
      if (error.response) {
        if (error.response.status === 429) {
          error.is429 = true;
        }
      }
      return Promise.reject(error);
    }
  );
};

/**
 * Instancia de axios para AGT Hall Backend (usuarios, pagos, archivos)
 * Puerto 5000 en desarrollo
 */
const apiHall = axios.create({
  baseURL: AGT_HALL_API_URL,
  timeout: STANDARD_TIMEOUT,
  withCredentials: true
});

// Aplicar interceptores
createErrorInterceptor(apiHall);

/**
 * Instancia de axios para AGT Chat Backend (proyectos)
 * Puerto 5001 en desarrollo
 */
const apiChat = axios.create({
  baseURL: AGT_CHAT_API_URL,
  timeout: STANDARD_TIMEOUT,
  withCredentials: true
});

// Aplicar interceptores
createErrorInterceptor(apiChat);

// Mantener 'api' como alias de apiHall para compatibilidad con código existente
const api = apiHall;

/**
 * Definimos los tipos de fondo válidos para las preferencias del usuario.
 * wallpaper-light y wallpaper-dark son variantes de wallpaper con tema forzado.
 */
const VALID_BACKGROUND_TYPES = ['system', 'light', 'dark', 'wallpaper', 'wallpaper-light', 'wallpaper-dark'];

/**
 * Función auxiliar para convertir Blob a Base64.
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    if (!(blob instanceof Blob)) {
      console.error('blobToBase64 recibió un valor que no es Blob:', blob);
      reject(new Error('Se esperaba un objeto Blob'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = reader.result;
        if (typeof result === 'string' && result.includes('base64,')) {
          resolve(result.split(',')[1]);
        } else {
          console.error('Formato inesperado en la conversión de blob:', result?.substring(0, 50));
          reject(new Error('Formato inválido en la conversión'));
        }
      } catch (e) {
        console.error('Error al procesar resultado de FileReader:', e);
        reject(e);
      }
    };
    reader.onerror = (e) => {
      console.error('Error en FileReader:', e);
      reject(e);
    };
    reader.readAsDataURL(blob);
  });
};

/* =========================================================================
   SUBIDA DE ARCHIVOS A S3
   ========================================================================= */

export const uploadPdfToS3 = async (pdfData, userId) => {
  try {
    // Convert PDF to base64 if needed
    const pdfBase64 = pdfData.data instanceof Blob
      ? await blobToBase64(pdfData.data)
      : pdfData.data;

    const payload = {
      pdf: pdfBase64,
      userId,
      fileName: pdfData.name,
      projectId: pdfData.projectId
    };

    const response = await api.post('/api/pdfs/upload', payload);

    if (!response.data || !response.data.success) {
      throw new Error('Failed to upload PDF');
    }

    // Return object with correct structure including s3Key
    return {
      s3Key: response.data.key, // <-- IMPORTANTE: s3Key
      s3Url: response.data.url,
      bucket: response.data.bucket,
      originalName: pdfData.name,
      type: pdfData.type,
      extractedText: response.data.extractedText
    };
  } catch (error) {
    throw new Error('Failed to upload PDF');
  }
};

export const uploadImageToS3 = async (imageData, userId) => {
  try {
    const payload = {
      image: imageData.data,
      userId,
      fileName: imageData.name,
      contentType: imageData.type,
      projectId: imageData.projectId
    };

    const response = await api.post('/api/images/upload', payload);

    if (!response.data || !response.data.success) {
      throw new Error('Failed to upload image');
    }

    return {
      ...response.data,
      originalName: imageData.name,
      type: imageData.type
    };
  } catch (error) {
    throw new Error('Failed to upload image');
  }
};

export const uploadAudioFile = async (audioBlob, userId) => {
  try {
    // Verificar que realmente tenemos un Blob válido
    if (!(audioBlob instanceof Blob)) {
      console.error('uploadAudioFile: audioBlob no es un Blob válido:', audioBlob);
      throw new Error('Invalid audio data');
    }

    // Convertir a base64
    const base64Audio = await blobToBase64(audioBlob);

    // Verificar que tenemos datos base64 válidos
    if (!base64Audio || base64Audio.length < 100) {
      console.error('Error: base64Audio parece inválido');
      throw new Error('Invalid base64 audio data');
    }

    const response = await api.post('/api/audio/upload', {
      audio: base64Audio,
      userId: userId
    });

    // Extraer claramente la URL de S3
    const s3Url = response.data.audioUrl || response.data.signedUrl || response.data.url || response.data.s3Url;
    if (!s3Url) {
      console.error('No S3 URL returned from audio upload:', response.data);
      throw new Error('No S3 URL in response');
    }

    // Devolvemos la respuesta completa con URL de S3 claramente identificada
    return {
      ...response.data,
      success: response.data.success || true,
      audioS3Url: s3Url, // Añadir explícitamente URL de S3
      audioUrl: s3Url,   // También como audioUrl para compatibilidad
      signedUrl: s3Url,  // También como signedUrl para compatibilidad
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error detallado en uploadAudioFile:', error);
    throw new Error('Failed to upload audio file: ' + (error.message || 'Unknown error'));
  }
};

/* =========================================================================
   GESTIÓN DE CATEGORÍAS DE USUARIO
   ========================================================================= */

/**
 * Obtiene las categorías personalizadas del usuario.
 */
export const getUserCategories = async (userId) => {
  try {
    const response = await api.get(`/api/user-categories/${userId}?uid=${userId}`);
    const customCategories = response.data.customCategories || [];
    return customCategories;
  } catch (error) {
    return [];
  }
};

/**
 * Crea una categoría personalizada.
 */
export const createUserCategory = async (userId, categoryName, iconName) => {
  try {
    const payload = { userId, categoryName, iconName };
    const response = await api.post('/api/user-categories', payload);
    return response.data.customCategories || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Elimina una categoría personalizada y reasigna los chats a otra categoría.
 */
export const deleteUserCategory = async (userId, categoryName, reassignTo) => {
  try {
    const payload = { userId, categoryName, reassignTo };
    const response = await api.delete('/api/user-categories', { data: payload });
    return response.data.customCategories || [];
  } catch (error) {
    throw error;
  }
};

/* =========================================================================
   TRANSCRIPCIÓN Y SÍNTESIS DE VOZ
   ========================================================================= */

/**
 * Transcribe audio
 */
export const transcribeAudio = async (audioData) => {
  try {
    const response = await api.post(
      '/api/transcribe',
      {
        audio: audioData
      },
      {
        timeout: 60000,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.data || !response.data.transcription) {
      throw new Error('No transcription data received');
    }

    return response.data.transcription;
  } catch (error) {
    if (error.response?.status === 413) {
      throw new Error(
        'El archivo de audio es demasiado grande. Por favor, intenta con una grabación más corta.'
      );
    } else if (error.code === 'ECONNABORTED') {
      throw new Error(
        'La transcripción está tomando más tiempo de lo esperado. Por favor, intenta de nuevo.'
      );
    }
    throw new Error('Error en la transcripción. Por favor, intenta de nuevo.');
  }
};

/**
 * Síntesis de voz (TTS)
 */
export const synthesizeSpeech = async (text, options = {}) => {
  try {
    const { voice = 'alloy', model = 'tts-1' } = options;

    const response = await api.post(
      '/api/voice/synthesize',
      {
        text,
        voice,
        model
      },
      {
        responseType: 'blob'
      }
    );

    return response.data;
  } catch (error) {
    throw new Error('Failed to synthesize speech');
  }
};

export const processAudioAndText = async (text, userId) => {
  try {
    const audioBlob = await synthesizeSpeech(text);
    const message = {
      text: text.trim(),
      audioBlob: audioBlob
    };
    return message;
  } catch (error) {
    throw new Error('Failed to process audio and text');
  }
};

/* =========================================================================
   GESTIÓN DE TÍTULOS Y RESÚMENES DE CHAT
   ========================================================================= */

/**
 * Actualiza el título de un chat
 */
export const updateChatTitle = async (userId, chatId, newTitle) => {
  try {
    const payload = {
      userId,
      uid: userId,
      chatId,
      newTitle
    };

    const response = await api.put('/api/update-chat-title', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to update chat title');
  }
};

/**
 * Genera un resumen para un chat
 */
export const generateChatSummary = async (userId, chatId, generateSummary = false, chatTitle) => {
  try {
    const payload = {
      userId,
      uid: userId,
      chatId,
      generateSummary,
      chatTitle,
      isFinal: false
    };

    const response = await api.post('/api/generate-summary', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to generate chat summary');
  }
};

/* =========================================================================
   FEEDBACK
   ========================================================================= */

/**
 * Envía feedback sobre un mensaje en particular (like/dislike + detalles)
 */
export const sendFeedback = async (userId, messageId, feedbackType, details, reason) => {
  if (!userId || !messageId || !feedbackType) {
    throw new Error('userId, messageId, and feedbackType are required');
  }

  try {
    const payload = {
      userId,
      uid: userId,
      messageId,
      feedbackType,
      details,
      reason
    };

    const response = await api.post('/api/feedback', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to send feedback');
  }
};

/* =========================================================================
   PREFERENCIAS DE USUARIO
   ========================================================================= */

/**
 * Ajusta la preferencia de fondo de usuario (system, light o dark).
 */
export const setUserBackgroundPreference = async (userId, backgroundType) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }


  if (!VALID_BACKGROUND_TYPES.includes(backgroundType)) {
    backgroundType = 'system';
  }

  try {
    const payload = {
      userId,
      uid: userId,
      backgroundType
    };

    const response = await api.put('/api/user-background', payload);
    return response.data;
  } catch (error) {
    console.error('[SERVICE] Error guardando preferencia de fondo:', error);
    throw new Error('Failed to set user background preference');
  }
};

/**
 * Obtiene la preferencia de fondo de un usuario.
 */
export const getUserBackgroundPreference = async (userId) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }


  try {
    const response = await api.get(`/api/user-background/${userId}?uid=${userId}`);
    const backgroundType = response.data.backgroundType;


    if (!backgroundType || !VALID_BACKGROUND_TYPES.includes(backgroundType)) {
      await setUserBackgroundPreference(userId, 'system');
      return 'system';
    }

    return backgroundType;
  } catch (error) {
    console.error('[SERVICE] getUserBackgroundPreference - Error:', error);
    if (error.response && error.response.status === 404) {
      await setUserBackgroundPreference(userId, 'system');
      return 'system';
    }
    return 'system';
  }
};

/**
 * Obtiene la preferencia de "personalidad" del usuario (normal, stoic, etc.)
 */
export const getUserPersonalityPreference = async (userId) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const response = await api.get(`/api/user-personality/${userId}?uid=${userId}`);
    const preference = response.data.personalityPreference;
    return preference;
  } catch (error) {
    return 'normal';
  }
};

/**
 * Ajusta la preferencia de personalidad del usuario.
 */
export const setUserPersonalityPreference = async (userId, personalityPreference) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const payload = {
      userId,
      uid: userId,
      personalityPreference
    };

    const response = await api.put('/api/user-personality', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to set user personality preference');
  }
};

/**
 * Actualiza la preferencia de modelo por defecto
 */
export const setDefaultModelPreference = async (userId, model) => {
  if (!userId || !model) {
    throw new Error('userId and model are required');
  }

  try {
    const payload = {
      userId,
      defaultModel: model
    };

    const response = await api.put('/api/user-model-preference', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to set default model preference');
  }
};

/**
 * Actualizar preferencias de notificaciones por email
 */
export const setEmailNotificationPreference = async (userId, enabled) => {
  try {
    const payload = {
      userId,
      emailNotifications: enabled
    };

    const response = await api.put('/api/user-email-notifications', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to update email notification settings');
  }
};

/**
 * Obtiene la preferencia de estilo de interfaz de un usuario.
 */
export const getUserInterfaceStyle = async (userId) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const response = await api.get(`/api/user-interface-style/${userId}?uid=${userId}`);
    return response.data.interfaceStyle || 'classic';
  } catch (error) {
    return 'classic'; // Valor por defecto si hay error
  }
};

/**
 * Actualiza la preferencia de estilo de interfaz del usuario.
 */
export const setUserInterfaceStyle = async (userId, interfaceStyle) => {
  if (!userId) {
    console.error('[AGTHallService] setUserInterfaceStyle: userId inválido');
    throw new Error('Invalid userId');
  }

  try {
    const payload = {
      userId,
      uid: userId,
      interfaceStyle
    };

    const response = await api.put('/api/user-interface-style', payload);
    return response.data;
  } catch (error) {
    console.error('[AGTHallService] Error en setUserInterfaceStyle:', error);
    throw new Error('Failed to set user interface style preference');
  }
};

/**
 * Obtiene la preferencia de pestaña de inicio del usuario.
 */
export const getUserStartupTabPreference = async (userId) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const response = await api.get(`/api/user-startup-tab/${userId}?uid=${userId}`);
    return response.data.startupTab || 'default';
  } catch (error) {
    if (error.response && error.response.status === 404) {
      await setUserStartupTabPreference(userId, 'default');
      return 'default';
    }
    return 'default';
  }
};

/**
 * Ajusta la preferencia de pestaña de inicio del usuario.
 */
export const setUserStartupTabPreference = async (userId, startupTab) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const payload = {
      userId,
      uid: userId,
      startupTab
    };

    const response = await api.put('/api/user-startup-tab', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to set user startup tab preference');
  }
};

/**
 * Obtiene la preferencia de idioma del usuario.
 */
export const getUserLanguagePreference = async (userId) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const response = await api.get(`/api/user-language/${userId}?uid=${userId}`);
    return response.data.language || 'auto';
  } catch (error) {
    if (error.response && error.response.status === 404) {
      await setUserLanguagePreference(userId, 'auto');
      return 'auto';
    }
    return 'auto';
  }
};

/**
 * Ajusta la preferencia de idioma del usuario.
 */
export const setUserLanguagePreference = async (userId, language) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const payload = {
      userId,
      uid: userId,
      language
    };

    const response = await api.put('/api/user-language', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to set user language preference');
  }
};

/**
 * Obtiene el estado de onboarding del usuario.
 */
export const getUserOnboardingStatus = async (userId) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const response = await api.get(`/api/user-onboarding/${userId}?uid=${userId}`);
    return response.data.status || 'pending';
  } catch (error) {
    return 'pending';
  }
};

/**
 * Obtiene estado y contexto de onboarding del usuario.
 */
export const getUserOnboardingData = async (userId) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const response = await api.get(`/api/user-onboarding/${userId}?uid=${userId}`);
    return {
      status: response.data.status || 'pending',
      onboardingData: response.data.onboardingData || null
    };
  } catch (error) {
    return {
      status: 'pending',
      onboardingData: null
    };
  }
};

/**
 * Actualiza el estado de onboarding del usuario (completed/skipped).
 */
export const setUserOnboardingStatus = async (userId, status, onboardingData = null) => {
  if (!userId || !status) {
    throw new Error('Invalid userId or status');
  }

  try {
    const payload = {
      userId,
      uid: userId,
      status
    };

    if (onboardingData && typeof onboardingData === 'object') {
      payload.onboardingData = onboardingData;
    }

    const response = await api.put('/api/user-onboarding', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to set user onboarding status');
  }
};

/**
 * Obtiene la preferencia de wallpaper del usuario (appartment, office, starbucks).
 */
export const getUserWallpaperPreference = async (userId) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const response = await api.get(`/api/user-wallpaper/${userId}?uid=${userId}`);
    return response.data.wallpaper || 'appartment';
  } catch (error) {
    if (error.response && error.response.status === 404) {
      await setUserWallpaperPreference(userId, 'appartment');
      return 'appartment';
    }
    return 'appartment';
  }
};

/**
 * Ajusta la preferencia de wallpaper del usuario (appartment, office, starbucks).
 */
export const setUserWallpaperPreference = async (userId, wallpaper) => {
  if (!userId) {
    throw new Error('Invalid userId');
  }

  try {
    const payload = {
      userId,
      uid: userId,
      wallpaper
    };

    const response = await api.put('/api/user-wallpaper', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to set user wallpaper preference');
  }
};

/**
 * Guarda las aplicaciones seleccionadas por el usuario
 */
export const saveUserSelectedApps = async (userId, selectedApps, defaultApp) => {
  try {
    const payload = {
      userId,
      selectedApps,
      defaultApp
    };

    const response = await api.put('/api/user-selected-apps', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to save selected apps');
  }
};

/**
 * Obtiene las aplicaciones seleccionadas por el usuario
 */
export const getUserSelectedApps = async (userId) => {
  try {
    const response = await api.get(`/api/user-selected-apps/${userId}?uid=${userId}`);
    return {
      selectedApps: response.data.selectedApps || ['home', 'chat'],
      defaultApp: response.data.defaultApp || 'home'
    };
  } catch (error) {
    return {
      selectedApps: ['home', 'chat'],
      defaultApp: 'home'
    };
  }
};

export const saveUserEmailToPreferences = async ({ userId, email, displayName }) => {
  try {
    const payload = {
      userId,
      email,
      displayName,
    };
    const response = await api.post('/api/user-preferences', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to save user preferences:', error);
    throw error;
  }
};

/* =========================================================================
   ESTADÍSTICAS Y DATOS DE USUARIO
   ========================================================================= */

/**
 * Obtiene estadísticas de uso (con soporte para caché y refresh)
 * @param {string} userId - ID del usuario
 * @param {boolean} forceRefresh - Si es true, recalcula las estadísticas
 */
export const getUserStatistics = async (userId, forceRefresh = false) => {
  try {
    const url = forceRefresh
      ? `/api/user-statistics/${userId}?forceRefresh=true`
      : `/api/user-statistics/${userId}`;

    const response = await api.get(url);
    return response.data;
  } catch (error) {
    return {
      status: 'error',
      totalChats: 0,
      totalMessages: 0,
      averageMessagesPerChat: 0,
      mostActiveDay: null,
      mostUsedApps: []
    };
  }
};

/**
 * Borrar historial de chat
 */
export const clearChatHistory = async (userId) => {
  try {
    const response = await api.delete(`/api/clear-user-history/${userId}`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to clear chat history');
  }
};

/**
 * Exportar datos de usuario
 */
export const exportUserData = async (userId) => {
  try {
    const response = await api.get(`/api/export-user-data/${userId}`, {
      responseType: 'blob'
    });

    // Crear un URL para descargar el archivo
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `agt-data-export-${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (error) {
    throw new Error('Failed to export user data');
  }
};

/* =========================================================================
   PAGOS Y SUSCRIPCIONES
   ========================================================================= */

/**
 * Crea una sesión de checkout para suscribirse a la versión PRO.
 */
export const createProCheckoutSession = async (userId) => {
  try {
    const payload = {
      userId,
      uid: userId,
      planType: 'pro'
    };

    const response = await api.post('/api/create-checkout-session', payload);
    return response.data.url;
  } catch (error) {
    throw new Error('Failed to create checkout session');
  }
};

/**
 * Verifica si el usuario es PRO.
 */
export const checkProStatus = async (userId) => {
  try {
    const response = await api.get(`/api/check-pro-status/${userId}?uid=${userId}`);
    return {
      isPro: response.data.isPro,
      proExpirationDate: response.data.proExpirationDate
    };
  } catch (error) {
    return { isPro: false };
  }
};

/* =========================================================================
   GESTIÓN DE PROYECTOS
   ========================================================================= */

/**
 * Obtiene los chats asociados a un proyecto específico.
 */
export const getProjectChats = async (userId, projectId) => {
  try {
    const response = await apiChat.get(`/api/projects/${projectId}/chats?userId=${userId}`);
    return {
      chats: response.data.chats,
      metadata: response.data.metadata
    };
  } catch (error) {
    return {
      chats: [],
      metadata: { totalChats: 0 }
    };
  }
};

// Crear nuevo proyecto
export const createProject = async (userId, name, category) => {
  try {
    const response = await apiChat.post('/api/projects', {
      userId,
      name,
      category
    });
    return response.data.project;
  } catch (error) {
    throw new Error('Failed to create project');
  }
};

// Obtener proyectos
export const getProjects = async (userId, category = null) => {
  try {
    const url = `/api/projects/${userId}${category ? `?category=${category}` : ''}`;
    const response = await apiChat.get(url);
    return response.data.projects;
  } catch (error) {
    return [];
  }
};

// Actualizar proyecto
export const updateProject = async (userId, projectId, name) => {
  try {
    const response = await apiChat.put(`/api/projects/${projectId}`, {
      userId,
      name
    });
    return response.data.project;
  } catch (error) {
    throw new Error('Failed to update project');
  }
};

// Eliminar proyecto
export const deleteProject = async (userId, projectId) => {
  try {
    const response = await apiChat.delete(`/api/projects/${projectId}?userId=${userId}`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to delete project');
  }
};

export const updateProjectContext = async ({
  userId,
  projectId,
  contextTitle,
  contextContent,
}) => {
  try {
    const payload = {
      userId,
      contextTitle,
      contextContent
    };

    const response = await apiChat.put(`/api/projects/${projectId}/context`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateProjectInstructions = async ({
  userId,
  projectId,
  instructionsTitle,
  instructionsContent
}) => {
  try {
    const payload = {
      userId,
      instructionsTitle,
      instructionsContent
    };

    const response = await apiChat.put(`/api/projects/${projectId}/instructions`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Obtiene la lista de archivos (imágenes, PDFs, etc.) de un proyecto en S3.
 */
export const getProjectFiles = async (userId, projectId) => {
  try {
    const response = await apiChat.get(`/api/projects/${projectId}/files?userId=${userId}`);
    return response.data.files || [];
  } catch (error) {
    console.error('Error fetching project files:', error);
    return [];
  }
};

export const deleteFile = async (fileType, key, userId, projectId = null) => {
  try {
    const queryParams = new URLSearchParams({
      userId,
      ...(projectId && { projectId })
    });

    const response = await api.delete(
      `/api/files/${fileType}/${encodeURIComponent(key)}?${queryParams}`
    );

    return response.data;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/* =========================================================================
   SERVICIOS DE PERFIL DE USUARIO
   ========================================================================= */

// Actualizar email del usuario
export const updateUserEmail = async (userId, newEmail) => {
  try {
    const payload = { userId, uid: userId, newEmail };
    const response = await api.put('/api/user-email', payload);
    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      throw new Error(error.response.data.error || 'Invalid email');
    }
    throw new Error('Failed to update email');
  }
};

// Actualizar perfil de usuario (nombre, foto)
// Obtener perfil del usuario
export const getUserProfile = async (userId) => {
  try {
    const response = await api.get(`/api/user-profile/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw new Error('Failed to get user profile');
  }
};

export const updateUserProfile = async (userId, profileData) => {
  try {
    const payload = {
      userId,
      uid: userId,
      displayName: profileData.displayName,
      photoURL: profileData.photoURL
    };
    const response = await api.put('/api/user-profile', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to update profile');
  }
};

// Subir foto de perfil
export const uploadProfilePhoto = async (userId, imageFile) => {
  try {
    // Convertir archivo a base64
    const base64Image = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });

    const payload = {
      userId,
      uid: userId,
      imageBase64: base64Image,
      fileName: imageFile.name
    };

    const response = await api.post('/api/upload-profile-photo', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to upload profile photo');
  }
};

// Actualizar configuración de privacidad
export const updatePrivacySettings = async (userId, privacySettings) => {
  try {
    const payload = { userId, uid: userId, ...privacySettings };
    const response = await api.put('/api/privacy-settings', payload);
    return response.data;
  } catch (error) {
    throw new Error('Failed to update privacy settings');
  }
};

// Obtener configuración de privacidad
export const getPrivacySettings = async (userId) => {
  try {
    const response = await api.get(`/api/privacy-settings/${userId}?uid=${userId}`);
    return response.data.privacySettings || {
      dataSharing: true,
      activityHistory: true,
      analyticsEnabled: true
    };
  } catch (error) {
    // Valores por defecto en caso de error
    return {
      dataSharing: true,
      activityHistory: true,
      analyticsEnabled: true
    };
  }
};

// Eliminar cuenta de usuario
export const deleteUserAccount = async (userId, confirmation) => {
  try {
    const response = await api.delete(`/api/delete-account/${userId}`, {
      data: { userId, uid: userId, confirmation }
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      throw new Error(error.response.data.error || 'Invalid confirmation');
    }
    throw new Error('Failed to delete account');
  }
};

/* =========================================================================
   EXPORT POR DEFECTO
   ========================================================================= */
const AGTHallService = {
  uploadPdfToS3,
  uploadImageToS3,
  uploadAudioFile,
  getUserCategories,
  createUserCategory,
  deleteUserCategory,
  transcribeAudio,
  synthesizeSpeech,
  processAudioAndText,
  updateChatTitle,
  generateChatSummary,
  sendFeedback,
  setUserBackgroundPreference,
  getUserBackgroundPreference,
  getUserPersonalityPreference,
  setUserPersonalityPreference,
  setDefaultModelPreference,
  setEmailNotificationPreference,
  getUserInterfaceStyle,
  setUserInterfaceStyle,
  getUserStartupTabPreference,
  setUserStartupTabPreference,
  getUserLanguagePreference,
  setUserLanguagePreference,
  getUserOnboardingStatus,
  setUserOnboardingStatus,
  getUserWallpaperPreference,
  setUserWallpaperPreference,
  saveUserSelectedApps,
  getUserSelectedApps,
  saveUserEmailToPreferences,
  getUserStatistics,
  clearChatHistory,
  exportUserData,
  createProCheckoutSession,
  checkProStatus,
  getProjectChats,
  createProject,
  getProjects,
  updateProject,
  deleteProject,
  updateProjectContext,
  updateProjectInstructions,
  getProjectFiles,
  deleteFile,
  updateUserEmail,
  getUserProfile,
  updateUserProfile,
  uploadProfilePhoto,
  updatePrivacySettings,
  getPrivacySettings,
  deleteUserAccount
};

export default AGTHallService;
